import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import WidgetRenderer, {
  WIDGET_DATA_DOCS,
} from "@/components/dashboard/widgets/WidgetRenderer";
import { WIDGET_DEFINITIONS } from "@/lib/widget-registry";
import { cn } from "@/lib/utils";

interface Props {
  widgetKey: string;
  size: "full" | "half" | "third";
  isSelected: boolean;
  onSelect: (key: string) => void;
  onCycleSize: (key: string) => void;
  onRemove: (key: string) => void;
}

const SIZE_LABEL: Record<"full" | "half" | "third", string> = {
  full: "Full",
  half: "½",
  third: "⅓",
};

/**
 * VIB-708-followup: Forent preview + redigering. Hver aktive widget
 * rendres live (samme som dashboard), men har en topplinje med
 * drag-handle, navn, size-toggle og fjern-knapp. Erstatter den gamle
 * Preview/Konfig-fanen.
 */
export function SortableWidgetPreviewCard({
  widgetKey,
  size,
  isSelected,
  onSelect,
  onCycleSize,
  onRemove,
}: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widgetKey });

  const def = WIDGET_DEFINITIONS.find((d) => d.key === widgetKey);
  if (!def) return null;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const isLive = !WIDGET_DATA_DOCS[widgetKey]?.hook.startsWith("Ny");

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "rounded-lg transition-all",
        isDragging && "opacity-50 shadow-lg ring-2 ring-primary z-10",
        isSelected && !isDragging && "ring-2 ring-primary/50 ring-offset-2 ring-offset-background",
      )}
      onClick={() => onSelect(widgetKey)}
    >
      {/* Toolbar — drag-handle, name + meta-badges, controls */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-t-lg border border-b-0 border-border">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="text-muted-foreground hover:text-foreground shrink-0 cursor-grab active:cursor-grabbing"
          aria-label="Dra for å endre rekkefølge"
          onClick={(e) => e.stopPropagation()}
        >
          <SrIcon name="grip-vertical" className="w-3.5 h-3.5" />
        </button>
        <span className="text-xs font-medium text-foreground/80 truncate">
          {def.name}
        </span>
        <Badge variant="outline" className="text-[10px] h-4 font-mono">
          {SIZE_LABEL[size]}
        </Badge>
        {isLive ? (
          <Badge
            variant="secondary"
            className="text-[10px] h-4 bg-emerald-500/10 text-emerald-500"
          >
            Live
          </Badge>
        ) : (
          <Badge
            variant="secondary"
            className="text-[10px] h-4 bg-amber-500/10 text-amber-500"
          >
            Placeholder
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-[10px] h-6 px-2"
            onClick={(e) => {
              e.stopPropagation();
              onCycleSize(widgetKey);
            }}
            title="Klikk for å endre størrelse"
          >
            Bytt størrelse
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(widgetKey);
            }}
            aria-label="Fjern widget"
          >
            <SrIcon name="x" className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      {/* Actual widget render */}
      <div className="border border-border rounded-b-lg overflow-hidden">
        <WidgetRenderer widgetKey={widgetKey} />
      </div>
    </div>
  );
}
